<template>
    <div class="tw:flex tw:flex-col tw:gap-4 tw:pb-4">
        <!-- Loading -->
        <CardFront v-if="loadingAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-8 tw:flex tw:items-center tw:justify-center tw:min-h-[300px]">
                <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                    <div class="tw:w-8 tw:h-8 tw:border-2 tw:border-gray-300 tw:border-t-blue-500 tw:rounded-full tw:animate-spin"></div>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        {{ $t('perfil.cambio_asiento.cargando_mapa') }}
                    </p>
                </div>
            </div>
        </CardFront>

        <!-- Error -->
        <CardFront v-else-if="errorAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-6">
                <div class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm">
                    {{ errorAsientos }}
                </div>
            </div>
        </CardFront>

        <!-- ═══════════════════════════════════════════ -->
        <!-- CAMINO tipo_svg=2: Leaflet 3D SVG           -->
        <!-- CAMINO tipo_svg=1 + proceso=1 + plazas:      -->
        <!--   Leaflet sobre SVG de la región (getSvgZona) -->
        <!--   con butacas DettallesButacas (patrón legacy  -->
        <!--   SeleccionarAsientosRegionAnexa). Si el SVG   -->
        <!--   está vacío (404) cae al grid de abajo.       -->
        <!-- ═══════════════════════════════════════════ -->
        <template v-else-if="tipoSvg === 2 || (tipoSvg === 1 && tipoProcesoOnline === 1 && tienePlazas && svgString)">
            <!-- SVG 3D seat map -->
            <template v-if="svgParsed">
                <CardFront custom-classes="tw:px-0 tw:py-0 tw:overflow-hidden">
                    <LeafletComponent
                        :svg-rendered="svgParsed"
                        :plazas-seleccionadas="plazasSeleccionadas"
                        :original-color="COLOR_LIBRE"
                        :disabled="false"
                        @is-panning="onIsPanning"
                    />
                </CardFront>

                <!-- Selected seat card -->
                <CardFront v-if="asientoSeleccionado" custom-classes="tw:px-0 tw:py-0">
                    <div class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3">
                        <div class="tw:flex tw:items-center tw:gap-3">
                            <div class="tw:w-9 tw:h-9 tw:rounded-full tw:bg-blue-900/20 tw:flex tw:items-center tw:justify-center">
                                <i class="material-icons tw:text-blue-900 tw:text-xl">event_seat</i>
                            </div>
                            <div>
                                <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-blue-900 tw:mb-0.5">
                                    {{ $t('perfil.cambio_asiento.butaca_seleccionada') }}
                                </p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">
                                    {{ zoneName }} — {{ $t('perfil.cambio_asiento.fila') }} {{ asientoSeleccionado.row || asientoSeleccionado.fila }} —
                                    {{ $t('perfil.cambio_asiento.butaca') }} {{ asientoSeleccionado.number || asientoSeleccionado.asiento }}
                                </p>
                            </div>
                        </div>
                        <button
                            class="tw:text-sm tw:text-gray-400 hover:tw:text-red-500 tw:transition-colors tw:cursor-pointer tw:border-0 tw:bg-transparent"
                            :title="$t('perfil.cambio_asiento.quitar_butaca')"
                            @click="deselectSeat"
                        >
                            <i class="material-icons tw:text-lg">close</i>
                        </button>
                    </div>

                    <!-- Tipo de abono selector (solo si múltiples opciones) -->
                    <template v-if="asientoSeleccionado && preciosDisponibles.length > 1">
                        <div class="tw:px-4 tw:pb-3">
                            <label class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1 tw:block">
                                {{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}
                            </label>
                            <select
                                class="tw:w-full tw:border tw:border-gray-200 tw:rounded-lg tw:px-3 tw:py-2 tw:text-sm tw:focus:border-blue-400 tw:focus:ring-1 tw:focus:ring-blue-400 tw:outline-none"
                                :value="tipoAbonoSeatId"
                                @change="$emit('update:tipo-abono-seat-id', Number($event.target.value))"
                            >
                                <option :value="null" disabled>{{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}</option>
                                <option
                                    v-for="precio in preciosDisponibles"
                                    :key="precio.id"
                                    :value="precio.id"
                                >
                                    {{ precio.descripcion }}
                                </option>
                            </select>
                        </div>
                    </template>
                </CardFront>

                <!-- Legend -->
                <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:px-1">
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0CBA31] tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_asiento.disponible') }}
                        </span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#CC0000] tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_asiento.ocupado') }}
                        </span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0F256E] tw:ring-2 tw:ring-blue-300 tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_asiento.seleccionado') }}
                        </span>
                    </div>
                </div>
            </template>

            <!-- Loading: SVG recibido pero aún parseándose (evita flash de empty state) -->
            <CardFront v-else-if="svgPreparando" custom-classes="tw:px-0 tw:py-0">
                <div class="tw:p-8 tw:flex tw:items-center tw:justify-center tw:min-h-[300px]">
                    <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                        <div class="tw:w-8 tw:h-8 tw:border-2 tw:border-gray-300 tw:border-t-blue-500 tw:rounded-full tw:animate-spin"></div>
                        <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                            {{ $t('perfil.cambio_asiento.cargando_mapa') }}
                        </p>
                    </div>
                </div>
            </CardFront>

            <!-- Empty state (no SVG parsed) -->
            <CardFront v-else custom-classes="tw:px-0 tw:py-0">
                <div class="tw:p-6 tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
                    <i class="material-icons tw:text-3xl tw:text-gray-300">event_busy</i>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        {{ $t('perfil.cambio_asiento.sin_butacas_disponibles') }}
                    </p>
                </div>
            </CardFront>
        </template>

        <!-- ═══════════════════════════════════════════════════════════════════ -->
        <!-- CAMINO tipo_svg!=2 (antifluo o null) o zona con plazas SIN SVG:     -->
        <!--   Grid de butacas (SelectorAsientosComponent). También es el        -->
        <!--   fallback cuando getSvgZona devuelve vacío/404 para una zona con   -->
        <!--   plazas (tipo_svg=1 + proceso=1 + tienePlazas): se renderiza el    -->
        <!--   grid con los datos de butacas DettallesButacas (caso borde).      -->
        <!-- Las plazas dentro de la zona se renderizan como huecos (asiento: 0|null). -->
        <!-- ═══════════════════════════════════════════════════════════════════ -->
        <template v-else>
            <TerrenoJuegoComponent
                :direccion-campo="zonaSeleccionada?.direccion_campo"
                :tipo-recinto="zonaSeleccionada?.tipo_recinto"
            />

            <CardFront custom-classes="tw:px-0 tw:py-0">
                <SelectorAsientosComponent
                    :loading="loadingAsientos"
                    :filas="filasConSeleccion"
                    :print_align="zonaSeleccionada?.print_align ?? 'center'"
                    :front="true"
                    @butacaSeleccionada="onSelectorAsientosToggle"
                />
            </CardFront>

            <!-- Selected seat card (when there is a selection) -->
            <CardFront v-if="asientoSeleccionado" custom-classes="tw:px-0 tw:py-0">
                <div class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3">
                    <div class="tw:flex tw:items-center tw:gap-3">
                        <div class="tw:w-9 tw:h-9 tw:rounded-full tw:bg-blue-900/20 tw:flex tw:items-center tw:justify-center">
                            <i class="material-icons tw:text-blue-900 tw:text-xl">event_seat</i>
                        </div>
                        <div>
                            <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-blue-900 tw:mb-0.5">
                                {{ $t('perfil.cambio_asiento.butaca_seleccionada') }}
                            </p>
                            <p class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">
                                {{ zoneName }} — {{ $t('perfil.cambio_asiento.fila') }} {{ asientoSeleccionado.row || asientoSeleccionado.fila }} —
                                {{ $t('perfil.cambio_asiento.butaca') }} {{ asientoSeleccionado.number || asientoSeleccionado.asiento }}
                            </p>
                        </div>
                    </div>
                    <button
                        class="tw:text-sm tw:text-gray-400 hover:tw:text-red-500 tw:transition-colors tw:cursor-pointer tw:border-0 tw:bg-transparent"
                        :title="$t('perfil.cambio_asiento.quitar_butaca')"
                        @click="$emit('quitar-asiento')"
                    >
                        <i class="material-icons tw:text-lg">close</i>
                    </button>
                </div>

                <!-- Tipo de abono selector (solo si múltiples opciones).
                     Lee `preciosDisponibles` (precios de la butaca, ya filtrados
                     por `abonadoDetail.descuento.renovacion` en el orquestador),
                     la MISMA lista que consume el guard `puedeAvanzarPasoButaca`
                     y que `nextStep` para derivar `tipo_abono_id`. Usar
                     `tiposAbonosDisponibles` aquí desincronizaba selector y
                     guard: el botón exigía elegir entre >1 precios y el
                     desplegable se pintaba con >1 tipos (lista distinta) que
                     podía venir vacía o con un solo elemento → NEXT bloqueado. -->
                <template v-if="asientoSeleccionado && preciosDisponibles.length > 1">
                    <div class="tw:px-4 tw:pb-3">
                        <label class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1 tw:block">
                            {{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}
                        </label>
                        <select
                            class="tw:w-full tw:border tw:border-gray-200 tw:rounded-lg tw:px-3 tw:py-2 tw:text-sm tw:focus:border-blue-400 tw:focus:ring-1 tw:focus:ring-blue-400 tw:outline-none"
                            :value="tipoAbonoSeatId"
                            @change="$emit('update:tipo-abono-seat-id', Number($event.target.value))"
                        >
                            <option :value="null" disabled>{{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}</option>
                            <option
                                v-for="precio in preciosDisponibles"
                                :key="precio.id"
                                :value="precio.id"
                            >
                                {{ precio.descripcion }}
                            </option>
                        </select>
                    </div>
                </template>
            </CardFront>

            <!-- Legend -->
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:px-1">
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0CBA31] tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_asiento.disponible') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#CC0000] tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_asiento.ocupado') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0F256E] tw:ring-2 tw:ring-blue-300 tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_asiento.seleccionado') }}
                    </span>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import LeafletComponent from '@/components/svg/components/LeafletComponent.vue';
    import SelectorAsientosComponent from '@/components/common/Asientos/SelectorAsientosComponent.vue';
    import TerrenoJuegoComponent from '@/components/common/Asientos/TerrenoJuegoComponent.vue';
    import { useSvgSeatMap } from '@/composables/front/perfil/useSvgSeatMap';

    // ─── Props ──────────────────────────────────────────────────────────────
    const props = defineProps({
        tipoSvg:                 { type: Number,  default: null },
        tipoProcesoOnline:       { type: Number,  default: null },
        tienePlazas:             { type: Boolean, default: false },
        asientosData:            { type: Object,  default: null },
        svgString:               { type: String,  default: '' },
        loadingAsientos:         { type: Boolean, default: false },
        errorAsientos:           { type: [String, Boolean], default: null },
        asientoSeleccionado:     { type: Object,  default: null },
        zonaSeleccionada:        { type: Object,  default: null },
        preciosDisponibles:      { type: Array,   default: () => [] },
        tipoAbonoSeatId:         { type: [Number, null], default: null },
    });

    const emit = defineEmits(['toggle-asiento', 'quitar-asiento', 'update:tipo-abono-seat-id']);

    // ─── SVG 3D seat map (tipo_svg=2) ────────────────────────────────────
    const {
        svgParsed,
        svgPreparando,
        onIsPanning,
        deselectSeat,
        COLOR_LIBRE,
    } = useSvgSeatMap(props, emit);

    // ─── Zone / seat display ──────────────────────────────────────────────

    const zoneName = computed(() =>
        props.zonaSeleccionada?.name ?? props.zonaSeleccionada?.nombre ?? ''
    );

    /**
     * Array con la butaca seleccionada (o vacío) para LeafletComponent.
     *
     * Contrato de la prop `plazasSeleccionadas`: array de objetos con `id`
     * (identidad para el diff del watch de Leaflet), `svg_id` (localiza el
     * círculo con `[id^="butaca-${svg_id}-"]`) y `precios` (si `length > 0`
     * restaura `originalColor`, si no `gray`). Pasar `[]` constante rompe la
     * cadena: el watch de Leaflet sobre `clonedPlazasSeleccionadas` nunca
     * dispara y la butaca queda marcada al quitar desde la X. Este computed
     * replica el patrón legacy (`SeleccionarAsientosRegionAnexa`): la X emite
     * `quitar-asiento`, el padre null-ifica `asientoSeleccionado` y este
     * computed vuelve a `[]`, disparando el repintado de Leaflet.
     */
    const plazasSeleccionadas = computed(() =>
        props.asientoSeleccionado ? [props.asientoSeleccionado] : []
    );

    /**
     * Marca visualmente la butaca seleccionada dentro de `configuracion_asientos`,
     * derivando de `props.asientoSeleccionado.id` para no mutar la prop.
     * `AsientoComponent` lee `asiento.seleccionado` para aplicar la clase CSS `seleccionada`.
     */
    const filasConSeleccion = computed(() => {
        const filas = props.asientosData?.configuracion_asientos ?? {};
        const selectedId = props.asientoSeleccionado?.id;
        const result = {};
        for (const fila of Object.keys(filas)) {
            result[fila] = filas[fila].map(b => ({
                ...b,
                seleccionado: b.id === selectedId,
            }));
        }
        return result;
    });

    // ─── SelectorAsientosComponent interaction (tipo_svg!=2: antifluo o null) ─────────────

    /**
     * Recibe `(fila, asientoNro)` desde SelectorAsientosComponent → @butacaSeleccionada,
     * localiza el objeto butaca crudo en `asientosData.configuracion_asientos` y
     * lo emite al padre (`CambioButacaView`) con la forma esperada por
     * `useCambioAsientoAsientos.toggleAsiento({ id, fila, asiento, plaza_id, precios })`.
     *
     * Si la butaca es un `hueco` (es_hueco=true → asiento=null), no se emite selección:
     * el slot se conserva en el grid para respetar la distribución visual, pero
     * la plaza no es seleccionable desde el grid — su selección viene marcada
     * por la zona (`tiene_plazas=true`) y el orquestador bifurca el payload.
     *
     * @param {string|number} fila       Clave de fila (letra/número devuelto por el backend)
     * @param {string|number} asientoNro Número de butaca dentro de la fila (null para huecos)
     */
    function onSelectorAsientosToggle(fila, asientoNro) {
        const filas = props.asientosData?.configuracion_asientos ?? {};
        const butacasDeFila = filas[fila] ?? [];
        const target = butacasDeFila.find(b => b.asiento === asientoNro);
        if (!target) return;
        if (target.es_hueco) return;
        emit('toggle-asiento', {
            id: target.id,
            fila: target.fila,
            asiento: target.asiento,
            plaza_id: target.plaza_id,
            precios: target.precios ?? [],
        });
    }
</script>
